import {
  useEffect,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type FocusEvent,
  useId,
} from "react";
import {
  UseComboboxProps as UseDownshiftComboboxProps,
  UseSelectProps as UseDownshiftSelectProps,
  UseComboboxReturnValue as UseDownshiftComboboxReturnValue,
  UseSelectReturnValue as UseDownshiftSelectReturnValue,
} from "downshift";
import cx from "classnames";

import { useCombobox } from "./Combobox";

import { Popover, usePopoverContext } from "../Popover";
import { Divider } from "../Divider";

import styles from "./Combobox.module.scss";
import listboxStyles from "../Listbox/Listbox.module.scss";

import { supportsPopover } from "../../internal/functions/supportsPopover";
import { ComboboxLegacyContent } from "../ComboboxLegacy";
import { ComboboxGroupProvider } from "./internal/ComboboxGroupContext";
import { Text } from "../Text";

/**
 * Props for the options passed to the children render function
 */
export type ComboboxContentOptionsProps<Item> = {
  /** Props for the option including selection and disabled state */
  optionProps: ReturnType<
    UseDownshiftComboboxReturnValue<Item>["getItemProps"]
  > & { selected: boolean; disabled: boolean };
  /** The item to render */
  item: Item;
  /** The index of the item in the list */
  index: number;
};

/**
 * Props for the ComboboxContent component
 */
export type ComboboxContentProps<Item> = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> & {
  /** Render function that receives combobox state and returns content */
  children?: ({
    items,
    inputValue,
    selectedItem,
    getMenuProps,
    getItemProps,
  }: Pick<
    UseDownshiftComboboxProps<Item> | UseDownshiftSelectProps<Item>,
    "items"
  > &
    Pick<
      | UseDownshiftComboboxReturnValue<Item>
      | UseDownshiftSelectReturnValue<Item>,
      "getMenuProps" | "getItemProps" | "inputValue" | "selectedItem"
    > & {
      listProps: ReturnType<
        | UseDownshiftComboboxReturnValue<Item>["getMenuProps"]
        | UseDownshiftSelectReturnValue<Item>["getMenuProps"]
      > & { style: any };
      options: ComboboxContentOptionsProps<Item>[];
    }) => ReactNode;
  /** Whether to disable popover behavior and render inline */
  disablePopover?: boolean;
};

const ComboboxContentElement = function <Item = any>({
  children,
  className,
  disablePopover = false,
  ...rest
}: ComboboxContentProps<Item>) {
  const {
    items,
    itemToKey,
    inputValue,
    selectedItem,
    selectedItems,
    setHasDisabledPopover,
    getMenuProps,
    getItemProps,
    isDisabled,
    isReadOnly,
    contentRef,
    closeMenu,
    groups,
  } = useCombobox<Item>();

  const ComboboxPopoverContentClassNames = cx(
    styles["popover-content"],
    className,
  );

  useEffect(() => {
    setHasDisabledPopover(disablePopover);

    return () => {
      setHasDisabledPopover(false);
    };
  }, [disablePopover, setHasDisabledPopover]);

  const context = usePopoverContext();
  // if we focus out to something not within the reference or content divs
  // then close the popover
  const handleBlur = (_e: FocusEvent) => {
    if (context?.controlled) return;
    context?.popover.close?.(closeMenu);
  };

  const resolveChildren = (groupItems: Item[]) =>
    children?.({
      items: groupItems,
      inputValue,
      selectedItem,
      getMenuProps,
      getItemProps,
      listProps: {
        ...getMenuProps({}, { suppressRefError: true }),
        style: {
          display: groupItems.length ? undefined : "none",
          overflow: "auto",
        },
      },
      options: items.map((item, index) => ({
        optionProps: {
          ...getItemProps({
            item,
            index,
          }),
          tabIndex: disablePopover ? -1 : undefined,
          selected:
            itemToKey(selectedItem) === itemToKey(item) ||
            selectedItems.some(
              (selectedItem) => itemToKey(selectedItem) === itemToKey(item),
            ),
          disabled: !!isDisabled || !!isReadOnly, // also see "isItemDisabled" function
        },
        item,
        index,
      })),
    });

  const id = useId();

  return disablePopover ? (
    <div
      {...rest}
      className={ComboboxPopoverContentClassNames}
      ref={contentRef}
      onBlur={handleBlur}
    >
      {groups?.length
        ? groups.map((group, i) => (
            <ComboboxGroupProvider key={group.key} value={group}>
              <ul
                aria-labelledby={`${id}-group-${group.key}`}
                className={cx(styles["group"], listboxStyles["listbox"])}
                data-anv="combobox-group"
                role="group"
              >
                <li>
                  {group.label != null ? (
                    <Text
                      className={styles["group-title"]}
                      id={`${id}-group-${group.key}`}
                      variant="eyebrow"
                    >
                      {group.label}
                    </Text>
                  ) : i !== 0 ? (
                    <Divider />
                  ) : null}
                  {resolveChildren(group.items)}
                </li>
              </ul>
            </ComboboxGroupProvider>
          ))
        : resolveChildren(items)}
    </div>
  ) : (
    <Popover.Content
      data-anv="combobox-content"
      {...rest}
      className={ComboboxPopoverContentClassNames}
      ref={getMenuProps({ ref: contentRef }, { suppressRefError: true }).ref}
      tabIndex={-1}
      onBlur={handleBlur}
    >
      {groups?.length
        ? groups.map((group, i) => (
            <ComboboxGroupProvider key={group.key} value={group}>
              <ul
                aria-labelledby={`${id}-group-${group.key}`}
                className={cx(styles["group"], listboxStyles["listbox"])}
                data-anv="combobox-group"
                role="group"
              >
                <li>
                  {group.label != null ? (
                    <Text
                      className={styles["group-title"]}
                      id={`${id}-group-${group.key}`}
                      variant="eyebrow"
                    >
                      {group.label}
                    </Text>
                  ) : i !== 0 ? (
                    <Divider />
                  ) : null}
                  {resolveChildren(group.items)}
                </li>
              </ul>
            </ComboboxGroupProvider>
          ))
        : resolveChildren(items)}
    </Popover.Content>
  );
};

/**
 * ComboboxContent component for rendering the content area of a combobox
 *
 * Features:
 * - Supports both popover and inline rendering modes
 * - Provides render props pattern for flexible content rendering
 * - Handles grouped items with proper ARIA labels
 * - Integrates with combobox state management
 * - Supports custom styling and layout
 * - Manages focus and blur events for popover behavior
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered combobox content area
 *
 * @example
 * <Combobox.Content>
 *   {({ items }) => (
 *     <ul>
 *       {items.map((item, index) => (
 *         <li key={item.id}>
 *           {item.name}
 *         </li>
 *       ))}
 *     </ul>
 *   )}
 * </Combobox.Content>
 */
export const ComboboxContent = function <Item = any>({
  children,
  className,
  disablePopover = false,
  ...rest
}: ComboboxContentProps<Item>) {
  if (!supportsPopover())
    return (
      <ComboboxLegacyContent<Item>
        disablePopover={disablePopover}
        className={className}
        {...rest}
      >
        {children}
      </ComboboxLegacyContent>
    );

  return (
    <ComboboxContentElement<Item>
      className={className}
      disablePopover={disablePopover}
      {...rest}
    >
      {children}
    </ComboboxContentElement>
  );
};
